import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { getCalApi } from '@calcom/embed-react';
import PricingSection from '../components/landing/PricingSection';
import { IOS_APP_STORE_URL } from '../lib/appLinks';
import CategorySection from '../components/landing/CategorySection';
import FounderSection from '../components/landing/FounderSection';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowUpRight,
  BarChart3,
  Calendar,
  Check,
  HeartPulse,
  Menu,
  Minus,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  X,
  Zap,
} from 'lucide-react';

function buildRegisterHref(plan, billingPeriod) {
  const params = new URLSearchParams({
    plan: String(plan || 'starter').toLowerCase(),
    billing_period: billingPeriod === 'yearly' ? 'yearly' : 'monthly',
  });
  return `/register?${params.toString()}`;
}

const HERO_VIDEO = 'https://assets.mixkit.co/videos/17469/17469-720.mp4';
const SOLUTION_VIDEO = 'https://assets.mixkit.co/videos/4834/4834-720.mp4';
const AUDIENCE_VIDEO = 'https://assets.mixkit.co/videos/46748/46748-720.mp4';
const HERO_POSTER = 'https://assets.mixkit.co/videos/17469/17469-thumb-720-0.jpg';
const SOLUTION_POSTER = 'https://assets.mixkit.co/videos/4834/4834-thumb-720-0.jpg';
const AUDIENCE_POSTER = 'https://assets.mixkit.co/videos/46748/46748-thumb-720-2.jpg';

const uspImages = {
  design: '/images/Formulare_Beispiel.webp',
  booking: '/images/Buchungseite_beispiel.webp',
  records: '/images/kunden_beispiel.webp',
  automation: '/images/automation_Beispiel.webp',
  marketing: '/images/content_beispiel.webp',
};

const benefits = [
  {
    icon: Zap,
    title: 'Weniger Verwaltungsaufwand',
    desc: 'Weniger manuelle Rückfragen, weniger doppelte Arbeit, weniger Wechsel zwischen Tools.',
  },
  {
    icon: Sparkles,
    title: 'Mehr Professionalität nach außen',
    desc: 'Deine Praxis wirkt digital sauber, modern und vertrauenswürdig.',
  },
  {
    icon: HeartPulse,
    title: 'Bessere Patientenführung',
    desc: 'Von der Buchung bis zur Behandlung erleben Patienten einen klaren, angenehmen Ablauf.',
  },
  {
    icon: Users,
    title: 'Mehr Struktur im Team',
    desc: 'Wichtige Informationen sind dort verfügbar, wo sie gerade gebraucht werden.',
  },
  {
    icon: TrendingUp,
    title: 'Mehr Buchungschancen',
    desc: 'Ein hochwertiger Auftritt und ein einfacher Buchungsprozess machen aus Interesse echte Termine.',
  },
  {
    icon: ShieldCheck,
    title: 'Mehr Sicherheit im Ablauf',
    desc: 'Wichtige Informationen, Formulare, Dokumentation und Zahlungen bleiben sauber zusammen, damit weniger untergeht.',
  },
];

const audiencePoints = [
  'professioneller arbeiten wollen',
  'digital sauber aufgestellt sein möchten',
  'mehr brauchen als Standard-Booking',
  'keine schwere, unflexible Praxissoftware wollen',
  'wachsen möchten, ohne dass der Alltag chaotischer wird',
];

const trustPills = [
  'Online-Buchung',
  'Digitale Formulare & Unterschriften',
  'Patientenakte',
  'Automatische Erinnerungen',
  'Zahlungen & Anzahlungen',
  'Google Kalender-Sync',
  'Stripe-Zahlungen',
  'DSGVO-konform',
];

const usps = [
  {
    n: '01',
    title: 'Digitale & moderne Praxis',
    description: 'Patienten können online buchen, Formulare digital ausfüllen und direkt unterschreiben. Das macht den Ablauf moderner und professioneller, noch bevor die Behandlung beginnt.',
    items: ['Digitale Formulare', 'Digitale Unterschriften', 'Online-Terminbuchung', 'Stornierung und Erinnerungen', 'Beratungen und Behandlungen direkt buchbar'],
    image: uspImages.design,
  },
  {
    n: '02',
    title: 'Buchungsseite, die Vertrauen aufbaut',
    description: 'Eine Buchungsseite entscheidet mit darüber, ob aus Interesse wirklich eine Buchung wird. Make It Booking hilft, Behandlungen verständlich und hochwertig darzustellen.',
    items: ['Freie Termine sofort sichtbar', 'Behandlungen mit Bildern verständlich dargestellt', 'Farben und Design passend zur Praxis', 'Mobile Buchung ohne Reibung'],
    image: uspImages.booking,
  },
  {
    n: '03',
    title: 'Patientendaten & Dokumentation zentral',
    description: 'Patienten geben relevante Daten digital selbst ein. Dein Team hat Dokumentation, Notizen und Behandlungsdetails direkt im Terminfluss verfügbar.',
    items: ['Patienten geben Daten digital selbst ein', 'Alle Infos zentral gespeichert', 'Dokumentation direkt vor Ort per Tablet', 'GOÄ-Positionen direkt am Termin vorbereiten', 'Historie und Notizen jederzeit verfügbar'],
    image: uspImages.records,
  },
  {
    n: '04',
    title: 'Mehr Struktur durch Automatisierungen',
    description: 'Weniger Nacharbeit, mehr Verlässlichkeit. Erinnerungen, Bestätigungen und Stornologik laufen automatisch, ohne dass du daran denken musst.',
    items: ['Erinnerungen per E-Mail und SMS laufen automatisch', 'Instagram-Nachrichten direkt in der Anwendung automatisieren', 'Anzahlung bei der Buchung einziehen', 'Stornogebühren bei später Absage möglich', 'Wartelisten-Eintragung für ausgebuchte Zeiten'],
    image: uspImages.automation,
  },
  {
    n: '05',
    title: 'Marketing-Unterstützung für mehr Patienten',
    description: 'Make It Booking hört nicht bei der Verwaltung auf. Du bekommst zusätzlich Unterstützung für Sichtbarkeit und Patientengewinnung.',
    items: ['Vorlagen für Social Media, Ads und Content', 'Designs für Preislisten und Praxismaterialien', 'Kampagnen für Facebook und Instagram vorbereiten', 'Buchungs- und Marketingdaten verbinden'],
    image: uspImages.marketing,
  },
];

const standardTools = [
  'Nur Terminvergabe',
  'Formulare über externe Tools',
  'Keine Behandlungsdokumentation',
  'Patientendaten manuell pflegen',
  'Keine Zahlungs- oder Anzahlungslogik',
  'Termine ohne Absicherung',
  'Keine Social-Media-Automatisierung',
  'Marketing separat zukaufen',
];

const makeItBooking = [
  'Kompletter Patientenprozess von A bis Z',
  'Digitale Formulare & Unterschriften',
  'Behandlungsdoku mit Körper- & Gesichtskarten',
  'Patientenakte & Dokumentation zentral',
  'Zahlungen und Anzahlungen direkt bei der Buchung',
  'Automatisierungen & Erinnerungen ab Tag 1',
  'Instagram- & Social-Media-Automatisierungen',
  'Marketing & Ads-Vorlagen inklusive',
];

const faqs = [
  {
    question: 'Ist das nur ein Terminbuchungstool?',
    answer: 'Nein. Make It Booking verbindet Buchung, Patientenakte, Dokumentation, Automatisierungen, Zahlungen und Marketing-Unterstützung. Du bekommst ein System, keinen isolierten Kalender.',
  },
  {
    question: 'Ist das professionell genug für medizinische Ästhetik?',
    answer: 'Ja. Genau deshalb ist die Kombination aus Dokumentation, Patientenprozess, Zahlungslogik und professionellem Auftritt so wichtig. Make It Booking wurde mit Blick auf die Anforderungen ästhetischer Praxen aufgebaut. Für privatärztliche Leistungen lassen sich GOÄ-Positionen, Faktoren und erforderliche Begründungen direkt nach dem Termin vorbereiten. Die Rechnungsfunktion bleibt bis zur dokumentierten Freigabe durch Medizinrecht und Steuerberatung gesperrt.',
  },
  {
    question: 'Wird der Umstieg kompliziert?',
    answer: 'Nein, denn du machst ihn nicht allein. Wir richten deine Leistungen, Formulare, Mitarbeiter und die Buchungsseite gemeinsam mit dir ein und unterstützen dich in einer enthaltenen Einrichtungssitzung von 60 Minuten. Optional übernehmen wir bis zu 2.500 Patientenstammdatensätze für einmalig 500 € netto. Dokumente, Bilder, Termine und Datenbereinigung sind nicht enthalten. Google Kalender und Stripe lassen sich direkt verbinden.',
  },
  {
    question: 'Ist das zu groß für kleinere Praxen oder Standorte?',
    answer: 'Nein. Es ist gerade für Teams interessant, die professioneller arbeiten wollen, ohne sich mit unnötig schwerer Software zu belasten. Du nutzt nur, was du wirklich brauchst.',
  },
  {
    question: 'Was ist mit DSGVO?',
    answer: 'Datenschutz ist bei Patientendaten kein Extra, sondern Pflicht. Make It Booking ist DSGVO-konform aufgebaut, vom Zugriffsrecht pro Rolle bis zur Datenhaltung. Einen Auftragsverarbeitungsvertrag bekommst du beim Onboarding.',
  },
  {
    question: 'Kündigung und Laufzeit',
    answer: 'Alle Pläne sind monatlich kündbar. Bei Jahresverträgen gilt die jährliche Laufzeit mit Rabatt.',
  },
];

function VideoBackground({ src, poster, className = '', overlayClassName = '', opacity = 1 }) {
  const [failed, setFailed] = useState(false);

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {!failed && (
        <video
          className="h-full w-full object-cover"
          src={src}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => setFailed(true)}
          style={{ opacity }}
          aria-hidden="true"
        />
      )}
      {failed && (
        <img src={poster} alt="" className="h-full w-full object-cover" aria-hidden="true" />
      )}
      <div className={`absolute inset-0 ${overlayClassName}`} />
    </div>
  );
}

function SectionEyebrow({ children, dark = false }) {
  return (
    <p className={`mb-5 text-[11px] font-semibold uppercase tracking-[0.32em] ${dark ? 'text-[#F05A8A]' : 'text-[#F02672]'}`}>
      {children}
    </p>
  );
}

function GradientText({ children }) {
  return (
    <span className="landing-gradient-text inline-block bg-gradient-to-r from-[#F05A8A] via-[#EC2B72] to-[#C83CE0] bg-clip-text pb-[0.22em] pt-[0.08em] pl-[0.04em] pr-[0.16em] italic font-light leading-[1.2] text-transparent">
      {children}
    </span>
  );
}

function ScrollProgressBar() {
  useEffect(() => {
    const bar = document.getElementById('scroll-progress-bar');
    if (!bar) return;

    const update = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0 ? window.scrollY / scrollable : 0;
      bar.style.transform = `scaleX(${progress})`;
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div
      className="fixed left-0 top-0 z-[60] h-[3px] w-full bg-[#FFFBFC]/88 backdrop-blur-2xl"
      aria-hidden="true"
    >
      <div
        id="scroll-progress-bar"
        className="h-full w-full bg-gradient-to-r from-[#F02672] via-[#F05A8A] to-[#C83CE0]"
        style={{
          transform: 'scaleX(0)',
          transformOrigin: 'left center',
          transition: 'transform 12ms linear',
        }}
      />
    </div>
  );
}

function useLandingReveal() {
  useEffect(() => {
    const elements = Array.from(document.querySelectorAll('[data-reveal]'));

    if (!elements.length) return undefined;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);
}

function useParallax(selector, speed = 0.35) {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(selector));
    if (!els.length) return;

    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const y = window.scrollY;
        els.forEach((el) => {
          el.style.transform = `translateY(${y * speed}px)`;
        });
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, [selector, speed]);
}

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const selectedBillingPeriod = 'yearly';
  useLandingReveal();
  useParallax('[data-parallax-slow]', 0.18);
  useParallax('[data-parallax-med]', 0.32);

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({"namespace":"mib-erstgesprach"});
      cal("ui", {"theme":"light","cssVarsPerTheme":{"light":{"cal-brand":"#EC2B72"}},"hideEventTypeDetails":true,"layout":"month_view"});
    })();
  }, []);

  const smoothScroll = (e) => {
    const href = e.currentTarget.getAttribute('href');
    if (!href || !href.startsWith('#')) return;
    e.preventDefault();
    const target = document.querySelector(href);
    if (!target) return;
    const headerOffset = 80;
    const top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <div className="landing-page min-h-screen overflow-hidden bg-[#FFFBFC] text-[#111014]">
      <ScrollProgressBar />
      <header className="fixed left-0 right-0 top-[3px] z-50 border-b border-[#2A1720]/10 bg-[#FFFBFC]/88 shadow-[0_12px_40px_-34px_rgba(42,23,32,0.45)] backdrop-blur-2xl">
        <div className="mx-auto grid h-14 max-w-[1368px] grid-cols-[auto_1fr_auto] items-center gap-4 px-4 sm:h-16 sm:px-6 md:px-10 lg:px-14">
          <Link to="/" className="flex items-center" aria-label="Make It Booking">
            <img src="/images/mib-logo-full.png" alt="Make It Booking Logo" className="h-10 w-auto object-contain sm:h-11" />
          </Link>

          <nav className="hidden items-center justify-center gap-8 md:flex">
            <a href="#usp" onClick={smoothScroll} className="text-sm text-[#6F6067] transition-colors hover:text-[#171014]">Funktionen</a>
            <a href="#nutzen" onClick={smoothScroll} className="text-sm text-[#6F6067] transition-colors hover:text-[#171014]">Vorteile</a>
            <a href="#zielgruppe" onClick={smoothScroll} className="text-sm text-[#6F6067] transition-colors hover:text-[#171014]">Für wen</a>
            <a href="#vergleich" onClick={smoothScroll} className="text-sm text-[#6F6067] transition-colors hover:text-[#171014]">Vergleich</a>
            <a href="#preise" onClick={smoothScroll} className="text-sm text-[#6F6067] transition-colors hover:text-[#171014]">Preise</a>
            <a href="#faq" onClick={smoothScroll} className="text-sm text-[#6F6067] transition-colors hover:text-[#171014]">FAQ</a>
          </nav>

          {/* Desktop Navigation CTA */}
          <div className="hidden md:flex items-center justify-end gap-2 sm:gap-3">
            <Link
              to="/login"
              className="inline-flex h-9 items-center rounded-full border border-[#2A1720]/15 bg-white px-4 text-sm font-medium text-[#43353B] shadow-sm transition-all hover:bg-[#F8EEF2] hover:text-[#171014] hover:shadow"
            >
              Anmelden
            </Link>
            <button
              data-cal-link="makeit-media/mib-erstgesprach"
              data-cal-namespace="mib-erstgesprach"
              data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true","theme":"light"}'
              className="btn-shine h-10 rounded-full bg-[#0F0C10] px-4 text-sm font-medium text-white shadow-[0_14px_30px_-20px_rgba(15,12,16,0.75)] hover:bg-[#20161B] sm:h-11 sm:px-5"
            >
              Demo buchen
            </button>
          </div>

          {/* Mobile Menu Toggle Button */}
          <div className="flex md:hidden items-center justify-end">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#2A1720]/15 bg-white text-[#171014] shadow-sm transition-all hover:bg-[#F8EEF2]"
              aria-label="Menü öffnen"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {menuOpen && (
          <div className="absolute left-0 right-0 top-14 z-40 border-b border-[#2A1720]/10 bg-[#FFFBFC] px-6 py-6 shadow-xl animate-in slide-in-from-top-5 duration-200 md:hidden">
            <nav className="flex flex-col gap-4">
              <a href="#usp" onClick={(e) => { smoothScroll(e); setMenuOpen(false); }} className="text-base font-medium text-[#6F6067] transition-colors hover:text-[#171014]">Funktionen</a>
              <a href="#nutzen" onClick={(e) => { smoothScroll(e); setMenuOpen(false); }} className="text-base font-medium text-[#6F6067] transition-colors hover:text-[#171014]">Vorteile</a>
              <a href="#zielgruppe" onClick={(e) => { smoothScroll(e); setMenuOpen(false); }} className="text-base font-medium text-[#6F6067] transition-colors hover:text-[#171014]">Für wen</a>
              <a href="#preise" onClick={(e) => { smoothScroll(e); setMenuOpen(false); }} className="text-base font-medium text-[#6F6067] transition-colors hover:text-[#171014]">Preise</a>
              <a href="#faq" onClick={(e) => { smoothScroll(e); setMenuOpen(false); }} className="text-base font-medium text-[#6F6067] transition-colors hover:text-[#171014]">FAQ</a>
              <div className="mt-4 border-t border-[#2A1720]/10 pt-4 flex flex-col gap-3">
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="flex h-12 items-center justify-center rounded-full border border-[#2A1720]/15 bg-white text-base font-medium text-[#43353B] shadow-sm hover:bg-[#F8EEF2]"
                >
                  Anmelden
                </Link>
                <button
                  data-cal-link="makeit-media/mib-erstgesprach"
                  data-cal-namespace="mib-erstgesprach"
                  data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true","theme":"light"}'
                  onClick={() => setMenuOpen(false)}
                  className="flex h-12 items-center justify-center rounded-full bg-[#171014] text-base font-medium text-white shadow hover:bg-[#20161B]"
                >
                  Demo buchen
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <section id="top" className="px-4 pb-12 pt-[calc(6rem+3px)] sm:px-6 md:px-10 md:pb-16 md:pt-[calc(7rem+3px)] lg:px-14">
        <div className="mx-auto max-w-[1440px]" data-reveal>
          <div className="dot-grid relative overflow-hidden rounded-[2rem] bg-[#171014] px-6 py-12 text-white sm:p-12 md:rounded-[2.5rem] md:p-16 lg:p-20">
            <VideoBackground
              src={HERO_VIDEO}
              poster={HERO_POSTER}
              opacity={0.34}
              overlayClassName="bg-gradient-to-br from-[#171014]/95 via-[#171014]/82 to-[#171014]/96"
            />
            <div className="pulse-glow absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full bg-[#D72D6B]/35 blur-[110px]" data-parallax-slow />
            <div className="pulse-glow absolute -bottom-24 -left-16 h-[380px] w-[380px] rounded-full bg-[#F05A8A]/20 blur-[100px] [animation-delay:1.4s]" data-parallax-med />
            <div className="relative grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 backdrop-blur-md">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F05A8A] opacity-70" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#F05A8A]" />
                  </span>
                  <span className="text-xs font-semibold tracking-wide text-white/85">Für moderne ästhetische Praxen</span>
                </div>

                <h1 className="mt-6 max-w-5xl overflow-visible pb-1 text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-[64px] xl:text-[72px]">
                  Die Praxissoftware für <br className="hidden lg:inline" />{' '}
                  <GradientText>medizinische Ästhetik</GradientText>
                </h1>
                <p className="mt-7 max-w-xl text-base leading-relaxed text-white/76 sm:text-lg">
                  <span className="font-semibold text-white">Du behandelst. Den Rest machen wir.</span> Online-Buchung, Patientenakte, digitale Formulare und Marketing in einem System.
                </p>

                <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    data-cal-link="makeit-media/mib-erstgesprach"
                    data-cal-namespace="mib-erstgesprach"
                    data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true","theme":"light"}'
                    className="btn-shine inline-flex items-center justify-center h-14 w-full rounded-full bg-[#EC2B72] px-7 text-[15px] font-medium text-white shadow-[0_14px_40px_-16px_rgba(236,43,114,0.85)] hover:bg-[#D72D6B] sm:w-auto"
                    data-testid="hero-cta"
                  >
                    Demo buchen
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </button>
                  <a
                    href="#preise"
                    onClick={smoothScroll}
                    className="inline-flex h-14 items-center justify-center rounded-full px-2 text-[15px] font-medium text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline sm:px-4"
                  >
                    Preise ansehen
                  </a>
                </div>

                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                  <a href={IOS_APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-white/75 underline-offset-4 hover:text-white hover:underline">
                    iPhone &amp; iPad: App Store <ArrowUpRight className="inline h-3.5 w-3.5" />
                  </a>
                  <Link to="/android-download" className="text-white/75 underline-offset-4 hover:text-white hover:underline">
                    Android-App herunterladen <ArrowUpRight className="inline h-3.5 w-3.5" />
                  </Link>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm">
                  <div className="flex items-center gap-2 text-white/70">
                    <Zap className="h-4 w-4 text-[#F05A8A]" strokeWidth={2.4} />
                    Datenübernahme kostenlos
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <ShieldCheck className="h-4 w-4 text-[#F05A8A]" strokeWidth={2.6} />
                    DSGVO-konform
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <Calendar className="h-4 w-4 text-[#F05A8A]" strokeWidth={2.4} />
                    Monatlich kündbar
                  </div>
                </div>
              </div>

              <div className="relative lg:col-span-5">
                <div className="relative mx-auto aspect-[5/6] w-full max-w-[460px] sm:aspect-square lg:aspect-[4/5]">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative w-full animate-[float_6s_ease-in-out_infinite] lg:scale-[1.04]">
                       <img
                         src="/images/hero_img.webp"
                         alt="Make It Booking Dashboard"
                         className="block h-auto w-full"
                       />
                    </div>
                  </div>
                  <div className="absolute -right-1 -top-4 flex items-center gap-3 rounded-2xl bg-white/95 px-4 py-3 text-[#171014] shadow-xl ring-1 ring-white/30 backdrop-blur-md sm:-right-4 lg:-right-6">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#D72D6B]/15">
                      <span className="text-[11px] font-bold text-[#D72D6B]">24/7</span>
                    </div>
                    <div>
                      <div className="text-[11px] text-[#7B6A72]">Online buchbar</div>
                      <div className="text-sm font-semibold">Ohne Anruf</div>
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-0 hidden items-center gap-2.5 rounded-2xl bg-white/95 px-3.5 py-2.5 text-[#171014] shadow-xl ring-1 ring-white/30 backdrop-blur-md sm:flex lg:-left-8">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F9DCE8]">
                      <Sparkles className="h-4 w-4 text-[#D72D6B]" />
                    </div>
                    <div>
                      <div className="text-[11px] text-[#7B6A72]">DSGVO</div>
                      <div className="text-sm font-semibold">konform</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative border-y border-[#2A1720]/10 bg-white/70 py-10 md:py-12" data-testid="trust-bar">
        <p className="mb-6 text-center text-sm md:text-base font-semibold uppercase tracking-[0.2em] text-[#F02672]">Für Praxen und Standorte, die mehr brauchen als einen einfachen Terminkalender</p>
        <div className="w-full overflow-hidden py-4">
          <div className="flex w-max animate-[marquee_36s_linear_infinite] gap-3 px-3">
            {[...trustPills, ...trustPills].map((pill, index) => (
              <span
                key={`${pill}-${index}`}
                className="trust-pill inline-flex h-11 items-center gap-2 rounded-full border border-[#2A1720]/10 bg-[#FFFBFC] px-5 text-sm font-medium text-[#5D4E55] shadow-sm"
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#F02672]" aria-hidden="true" />
                {pill}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20 md:px-10 md:py-28 lg:px-14">
        <div className="mx-auto grid max-w-[1280px] items-start gap-10 lg:grid-cols-12 lg:gap-16" data-reveal>
          <div className="lg:col-span-5">
            <SectionEyebrow>Das Problem</SectionEyebrow>
            <h2 className="text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
              Wenn die Praxis wächst, <GradientText>reicht ein einfacher Kalender nicht mehr.</GradientText>
            </h2>
          </div>
          <div className="space-y-8 text-lg leading-relaxed text-[#6F6067] lg:col-span-7">
            <p>Termine hier, Patientendaten dort, Formulare separat, Fotos auf anderen Wegen, Zahlungen wieder in einem anderen System.</p>
            <div className="rounded-3xl border border-[#2A1720]/10 bg-white p-6 shadow-[0_18px_60px_-40px_rgba(42,23,32,0.35)] md:p-8">
              <p className="mb-6 text-sm font-semibold text-[#171014]">Das kostet jeden Tag:</p>
              <div className="grid gap-5 sm:grid-cols-2">
                {[
                  ['Zeit', Calendar],
                  ['Konzentration', HeartPulse],
                  ['Übersicht', BarChart3],
                  ['Vertrauen im Ablauf', ShieldCheck],
                ].map(([label, Icon]) => (
                  <div key={label} className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FADCE8] text-[#D72D6B]">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-base text-[#171014]">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <p><span className="font-medium text-[#171014]">Wer medizinische Ästhetik professionell anbietet, braucht nicht nur gute Behandlungen.</span> Der gesamte Prozess muss professionell wirken.</p>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#F8EEF2] px-6 py-20 md:px-10 md:py-28 lg:px-14">
        <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-[#D72D6B]/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-[1280px] items-center gap-12 lg:grid-cols-12 lg:gap-16" data-reveal>
          <div className="order-2 lg:col-span-5 lg:order-1">
            <div className="relative mx-auto aspect-[4/5] w-full max-w-[480px] overflow-hidden rounded-[2rem] border border-[#2A1720]/10 shadow-[0_30px_80px_-35px_rgba(42,23,32,0.45)]">
              <img
                src="/images/lp_mib_all.webp"
                alt="Luxury medical aesthetics clinic interior"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#171014]/28 via-[#171014]/6 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3 rounded-2xl border border-white bg-white/95 p-4 shadow-lg backdrop-blur-md">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#D72D6B] text-white">
                  <Check className="h-5 w-5" strokeWidth={3} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#171014]">Alles an einem Ort</div>
                  <div className="text-xs text-[#7B6A72]">Buchung · Akte · Zahlung · Marketing</div>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:col-span-7 lg:order-2">
            <SectionEyebrow>Die Lösung</SectionEyebrow>
            <h2 className="text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
              Make It Booking bringt den ganzen <GradientText>Patientenprozess</GradientText> an einen Ort.
            </h2>
            <div className="mt-8 space-y-3 text-lg leading-relaxed text-[#6F6067]">
              <p>Von der Buchung bis zur Dokumentation.</p>
              <p>Von der Erinnerung bis zur Zahlung.</p>
              <p>Von der ersten Anfrage bis zur laufenden Betreuung.</p>
            </div>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#43353B]">
              So entsteht ein Ablauf, der für dein Team leichter wird und für Patientinnen klarer, moderner und vertrauenswürdiger wirkt.
            </p>
            <button
              data-cal-link="makeit-media/mib-erstgesprach"
              data-cal-namespace="mib-erstgesprach"
              data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true","theme":"light"}'
              className="btn-shine mt-9 inline-flex items-center justify-center h-14 rounded-full bg-[#171014] px-8 text-[15px] font-medium text-white shadow hover:bg-[#2A1720]"
            >
              Demo buchen
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <section id="usp" className="px-6 py-20 md:px-10 md:py-28 lg:px-14" data-testid="usp-section">
        <div className="mx-auto max-w-[1280px]" data-reveal>
          <div className="mb-14 max-w-3xl md:mb-16">
            <SectionEyebrow>Warum Make It Booking</SectionEyebrow>
            <h2 className="text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
              Warum Make It Booking für <GradientText>ästhetische Praxen</GradientText> so stark ist.
            </h2>
          </div>

          <div className="space-y-16 md:space-y-24">
            {usps.map((u, i) => {
              const reverse = i % 2 === 1;
              return (
                <div key={u.n} className="grid items-center gap-8 lg:grid-cols-12 lg:gap-14" data-reveal data-testid={`usp-item-${i}`}>
                  <div className={`lg:col-span-5 ${reverse ? 'lg:order-2' : ''}`}>
                    <div className="relative mx-auto aspect-[5/4] w-full max-w-[520px] overflow-hidden rounded-[1.75rem] border border-[#2A1720]/10 bg-[#F8EEF2] shadow-[0_18px_60px_-34px_rgba(42,23,32,0.45)] lg:max-w-[400px] xl:max-w-[430px] glow-card">
                      <img src={u.image} alt={u.title} className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.035]" />
                      <div className="absolute left-4 top-4 inline-flex h-8 items-center rounded-full bg-white/90 px-2.5 text-[11px] font-semibold text-[#D72D6B] backdrop-blur-sm">
                        {u.n}
                      </div>
                    </div>
                  </div>
                  <div className={`lg:col-span-7 ${reverse ? 'lg:order-1' : ''}`}>
                    <div className="mb-4 bg-gradient-to-r from-[#F05A8A] to-[#9A174D] bg-clip-text text-6xl font-bold leading-none text-transparent opacity-90 lg:text-7xl">
                      {u.n}
                    </div>
                    <h3 className="text-2xl font-bold leading-snug tracking-tight sm:text-3xl">{u.title}</h3>
                    <p className="mt-4 max-w-xl text-base leading-relaxed text-[#6F6067] md:text-lg">{u.description}</p>
                    <div className="mt-7 max-w-xl border-t border-[#2A1720]/10 pt-7">
                      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#6F6067]">Enthalten</p>
                      <ul className="space-y-3">
                        {u.items.map((item) => (
                          <li key={item} className="flex items-start gap-3">
                            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#D72D6B]/15 text-[#D72D6B]">
                              <Check className="h-3 w-3" strokeWidth={3} />
                            </span>
                            <span className="text-[#43353B]">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="nutzen" className="relative overflow-hidden px-6 py-20 md:px-10 md:py-28 lg:px-14">
        <div className="mx-auto max-w-[1280px]" data-reveal>
          <div className="mb-14 max-w-3xl">
            <SectionEyebrow>Nutzen</SectionEyebrow>
            <h2 className="text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
              Was sich im Alltag <GradientText>konkret verbessert.</GradientText>
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              const isDark = index % 2 === 1;
              return (
                <div
                  key={benefit.title}
                  className={`benefit-card glow-card rounded-3xl p-7 md:p-8 ${isDark ? 'bg-[#171014] text-white' : 'border border-[#2A1720]/10 bg-white shadow-[0_8px_30px_-18px_rgba(42,23,32,0.25)]'}`}
                >
                  <div className={`mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl ${isDark ? 'bg-[#D72D6B]/25 text-white' : 'bg-[#D72D6B]/15 text-[#D72D6B]'}`}>
                    <Icon className="h-5 w-5" strokeWidth={1.8} />
                  </div>
                  <h3 className={`text-xl font-bold leading-snug tracking-tight ${isDark ? 'text-white' : 'text-[#171014]'}`}>{benefit.title}</h3>
                  <p className={`mt-3 text-[15px] leading-relaxed ${isDark ? 'text-white/75' : 'text-[#6F6067]'}`}>{benefit.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative px-6 py-20 md:px-10 md:py-28 lg:px-14" id="zielgruppe">
        <div className="mx-auto max-w-[1280px]" data-reveal>
          <div className="dot-grid relative overflow-hidden rounded-[2rem] bg-[#171014] p-8 text-white md:rounded-[2.5rem] md:p-16 lg:p-20">
            <VideoBackground
              src={AUDIENCE_VIDEO}
              poster={AUDIENCE_POSTER}
              opacity={0.32}
              overlayClassName="bg-gradient-to-br from-[#171014]/90 via-[#171014]/78 to-[#171014]/95"
            />
            <div className="pulse-glow absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#D72D6B]/35 blur-[90px]" />
            <div className="pulse-glow absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-[#F05A8A]/15 blur-[90px] [animation-delay:1.2s]" />
            <div className="relative grid items-start gap-12 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <SectionEyebrow dark>Für wen es gemacht ist</SectionEyebrow>
                <h2 className="text-3xl font-semibold leading-[1.1] tracking-tight sm:text-4xl lg:text-[44px]">
                  Für medizinische Ästhetik entwickelt. <GradientText>Nicht für Salons</GradientText> umgebaut.
                </h2>
              </div>
              <div className="lg:col-span-7">
                <p className="mb-8 text-lg leading-relaxed text-white/80">Make It Booking passt besonders gut zu Praxen und Standorten, die:</p>
                <ul className="space-y-4">
                  {audiencePoints.map((point) => (
                    <li key={point} className="flex items-start gap-4">
                      <span className="mt-2 inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-[#F05A8A]" />
                      <span className="text-lg text-white/95">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F8EEF2] px-6 pb-20 pt-20 md:px-10 md:pb-28 md:pt-28 lg:px-14">
        <div className="mx-auto max-w-[1280px]" data-reveal>
          <div className="mb-12 max-w-3xl">
            <SectionEyebrow>Was Make It Booking von anderen unterscheidet</SectionEyebrow>
            <h2 className="text-3xl font-semibold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
              Warum nicht einfach ein <GradientText>Standard-Kalenderanbieter?</GradientText>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#6F6067]">
              Weil Standard-Tools oft bei der Terminvergabe aufhören. Make It Booking geht weiter.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 md:gap-6">
            <div className="rounded-3xl border border-[#2A1720]/10 bg-white p-8 shadow-[0_8px_30px_-18px_rgba(42,23,32,0.2)] md:p-10 glow-card">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#7B6A72]">Andere Tools</p>
              <h3 className="mb-7 text-2xl font-bold tracking-tight">Standard-Kalender</h3>
              <ul className="space-y-4">
                {standardTools.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F3EDF0] text-[#7B6A72]">
                      <Minus className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-[#6F6067]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative overflow-hidden rounded-3xl bg-[#171014] p-8 text-white md:p-10 glow-card">
              <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-[#D72D6B]/45 blur-3xl" />
              <p className="relative mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#F05A8A]">Unser Ansatz</p>
              <h3 className="relative mb-7 text-2xl font-bold tracking-tight">Make It Booking</h3>
              <ul className="relative space-y-4">
                {makeItBooking.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#D72D6B] text-white">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    <span className="text-white/95">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CategorySection />

      <PricingSection />

      <FounderSection />

      <section id="faq" className="px-6 py-20 md:px-10 md:py-28 lg:px-14">
        <div className="mx-auto grid max-w-[1200px] items-start gap-12 lg:grid-cols-12 lg:gap-20">
          <div className="lg:sticky lg:top-[calc(3px+5rem)] lg:col-span-5 lg:self-start" data-reveal>
            <SectionEyebrow>Häufige Fragen</SectionEyebrow>
            <h2 className="text-3xl font-semibold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
              Häufige Fragen. <GradientText>Ehrliche Antworten.</GradientText>
            </h2>
          </div>
          <div className="lg:col-span-7" data-reveal>
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={faq.question}
                  value={`faq-${index}`}
                  className="rounded-2xl border border-[#2A1720]/10 bg-white px-5 transition-all data-[state=open]:border-[#D72D6B]/30 data-[state=open]:shadow-[0_12px_36px_-18px_rgba(215,45,107,0.25)] md:px-7 glow-card"
                  data-testid={`faq-${index}`}
                >
                  <AccordionTrigger className="faq-trigger py-5 text-left text-lg font-medium tracking-tight text-[#171014] transition-colors hover:text-[#D72D6B] hover:no-underline md:text-xl">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pr-4 text-base leading-relaxed text-[#6F6067]">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="px-6 pb-20 md:px-10 md:pb-28 lg:px-14">
        <div className="mx-auto max-w-[1200px] overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#FFE1EC] via-[#FFF7FA] to-[#F8EEF2] p-8 text-center shadow-[0_20px_80px_-50px_rgba(42,23,32,0.45)] md:p-14" data-reveal>
          <SectionEyebrow>Bereit für den nächsten Schritt?</SectionEyebrow>
          <h2 className="mx-auto max-w-3xl text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
            Wenn deine Praxis modern wirken soll, muss auch der <GradientText>Ablauf modern</GradientText> sein.
          </h2>
          <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-[#6F6067]">
            Make It Booking hilft dir, Buchung, Patientenprozess, Dokumentation, Zahlungen und Wachstum an einem Ort zusammenzubringen.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              data-cal-link="makeit-media/mib-erstgesprach"
              data-cal-namespace="mib-erstgesprach"
              data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true","theme":"light"}'
              className="btn-shine inline-flex items-center justify-center h-14 rounded-full bg-[#171014] px-8 text-[15px] font-medium text-white shadow-[0_12px_30px_-16px_rgba(215,45,107,0.6)] hover:bg-[#2A1720]"
            >
              Demo buchen
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </button>
            <Link to={buildRegisterHref('starter', selectedBillingPeriod)}>
              <Button variant="outline" className="h-14 rounded-full border-[#2A1720]/15 bg-white/80 px-8 text-[15px] font-medium text-[#171014] backdrop-blur hover:bg-white hover:!text-[#171014]">
                Anmelden
              </Button>
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[#6F6067]">
            <span>Für mehr Struktur im Team</span>
            <span>Für weniger Reibung im Alltag</span>
            <span>Für einen professionellen Eindruck</span>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2A1720]/10 bg-[#F8EEF2]/60 px-6 py-14 md:px-10 lg:px-14">
        <div className="mx-auto max-w-[1440px]">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
            <Link to="/" className="flex items-center">
              <img src="/images/mib-logo-full.png" alt="Make It Booking Logo" className="h-11 w-auto object-contain" />
            </Link>
            <nav className="flex flex-wrap gap-x-7 gap-y-3 text-sm text-[#6F6067]">
              <a href="#usp" onClick={smoothScroll} className="transition-colors hover:text-[#171014]">Funktionen</a>
              <a href="#nutzen" onClick={smoothScroll} className="transition-colors hover:text-[#171014]">Vorteile</a>
              <a href="#zielgruppe" onClick={smoothScroll} className="transition-colors hover:text-[#171014]">Für wen</a>
              <a href="#faq" onClick={smoothScroll} className="transition-colors hover:text-[#171014]">FAQ</a>
              <a href={IOS_APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-[#171014]">iOS-App</a>
              <Link to="/android-download" className="transition-colors hover:text-[#171014]">Android-App</Link>
              <Link to="/datenschutz" className="transition-colors hover:text-[#171014]">Datenschutz</Link>
              <Link to="/impressum" className="transition-colors hover:text-[#171014]">Impressum</Link>
              <Link to="/nutzungsbedingungen" className="transition-colors hover:text-[#171014]">AGB</Link>
            </nav>
            <p className="text-xs text-[#6F6067]">© {new Date().getFullYear()} Make It Booking. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
